import { useState } from 'react'
import { Check, X, Pencil, UserCheck, UserX } from 'lucide-react'
import { Badge } from '@/components/ui/Badge'
import { Button } from '@/components/ui/Button'
import { PageSpinner } from '@/components/ui/Spinner'
import { useTeamMembers, useSaveTeamMember } from '@/hooks/useTeam'
import { useAuthStore } from '@/stores/authStore'
import type { AppUser, Role } from '@/types/app'

const ROLE_LABELS: Record<Role, string> = {
  owner:     'Owner',
  gm:        'General Manager',
  ops:       'Operations',
  chef:      'Chef',
  marketing: 'Marketing',
}

const ROLE_VARIANTS: Record<Role, 'neutral'|'info'|'warning'|'success'|'danger'> = {
  owner:     'danger',
  gm:        'info',
  ops:       'warning',
  chef:      'success',
  marketing: 'neutral',
}

const ROLE_EMAIL: Record<Role, string> = {
  owner:     'admin@amruthahar.com',
  gm:        'gm@amruthahar.com',
  ops:       'operations@amruthahar.com',
  chef:      'chef@amruthahar.com',
  marketing: 'marketing@amruthahar.com',
}

function EditRow({
  member,
  onDone,
}: {
  member: AppUser
  onDone: () => void
}) {
  const { mutateAsync, isPending } = useSaveTeamMember()
  const [name, setName]   = useState(member.full_name)
  const [phone, setPhone] = useState(member.phone ?? '')
  const [role, setRole]   = useState<Role>(member.role)

  async function save() {
    await mutateAsync({ id: member.id, full_name: name, phone: phone || null, role })
    onDone()
  }

  return (
    <tr className="bg-brand-50">
      <td className="px-4 py-2">
        <input
          value={name}
          onChange={e => setName(e.target.value)}
          className="w-full border border-stone-300 rounded px-2 py-1 text-sm focus:outline-none focus:ring-1 focus:ring-brand-400"
        />
      </td>
      <td className="px-4 py-2">
        <select
          value={role}
          onChange={e => setRole(e.target.value as Role)}
          className="w-full border border-stone-300 rounded px-2 py-1 text-sm focus:outline-none focus:ring-1 focus:ring-brand-400"
        >
          {(Object.keys(ROLE_LABELS) as Role[]).map(r => (
            <option key={r} value={r}>{ROLE_LABELS[r]}</option>
          ))}
        </select>
      </td>
      <td className="px-4 py-2">
        <input
          value={phone}
          onChange={e => setPhone(e.target.value)}
          placeholder="Phone number"
          className="w-full border border-stone-300 rounded px-2 py-1 text-sm focus:outline-none focus:ring-1 focus:ring-brand-400"
        />
      </td>
      <td className="px-4 py-2 text-stone-400 text-xs">{ROLE_EMAIL[member.role]}</td>
      <td className="px-4 py-2" />
      <td className="px-4 py-2">
        <div className="flex gap-1">
          <button onClick={save} disabled={isPending} className="p-1.5 rounded bg-brand-600 text-white hover:bg-brand-700">
            <Check size={13} />
          </button>
          <button onClick={onDone} className="p-1.5 rounded bg-stone-100 text-stone-600 hover:bg-stone-200">
            <X size={13} />
          </button>
        </div>
      </td>
    </tr>
  )
}

export function TeamPage() {
  const { data: members = [], isLoading } = useTeamMembers()
  const { mutateAsync: save }             = useSaveTeamMember()
  const currentUser = useAuthStore(s => s.user)
  const [editing, setEditing] = useState<string | null>(null)

  if (isLoading) return <PageSpinner />

  const active   = members.filter(m => m.is_active)
  const inactive = members.filter(m => !m.is_active)

  function MemberRow({ m }: { m: AppUser }) {
    if (editing === m.id) return <EditRow member={m} onDone={() => setEditing(null)} />
    const lastSeen = m.last_seen_at
      ? new Date(m.last_seen_at).toLocaleDateString()
      : 'Never'
    return (
      <tr className={['hover:bg-stone-50 transition-colors', !m.is_active ? 'opacity-50' : ''].join(' ')}>
        <td className="px-4 py-3">
          <div className="flex items-center gap-3">
            <div className="h-8 w-8 rounded-full bg-brand-100 flex items-center justify-center shrink-0">
              <span className="text-xs font-semibold text-brand-700">{m.full_name[0]?.toUpperCase()}</span>
            </div>
            <span className="font-medium text-stone-900">{m.full_name}</span>
          </div>
        </td>
        <td className="px-4 py-3">
          <Badge variant={ROLE_VARIANTS[m.role]}>{ROLE_LABELS[m.role]}</Badge>
        </td>
        <td className="px-4 py-3 text-sm text-stone-600">{m.phone ?? <span className="text-stone-300">—</span>}</td>
        <td className="px-4 py-3 text-xs text-stone-400 font-mono">{ROLE_EMAIL[m.role]}</td>
        <td className="px-4 py-3 text-xs text-stone-400">{lastSeen}</td>
        <td className="px-4 py-3">
          {currentUser?.id !== m.id && (
            <div className="flex items-center gap-1">
              <button
                onClick={() => setEditing(m.id)}
                className="p-1.5 rounded text-stone-400 hover:text-brand-600 hover:bg-brand-50 transition-colors"
                title="Edit"
              >
                <Pencil size={13} />
              </button>
              <button
                onClick={() => save({ id: m.id, is_active: !m.is_active })}
                className={[
                  'p-1.5 rounded transition-colors',
                  m.is_active
                    ? 'text-stone-400 hover:text-danger hover:bg-red-50'
                    : 'text-stone-400 hover:text-success-dark hover:bg-green-50',
                ].join(' ')}
                title={m.is_active ? 'Deactivate' : 'Reactivate'}
              >
                {m.is_active ? <UserX size={13} /> : <UserCheck size={13} />}
              </button>
            </div>
          )}
        </td>
      </tr>
    )
  }

  return (
    <div className="p-6 space-y-5">
      <div className="page-header">
        <h1 className="text-xl font-semibold text-stone-900">Team</h1>
        <span className="text-sm text-stone-500">{active.length} active member{active.length !== 1 ? 's' : ''}</span>
      </div>

      {/* Role access summary */}
      <div className="grid grid-cols-2 sm:grid-cols-5 gap-2">
        {(Object.entries(ROLE_LABELS) as [Role, string][]).map(([role, label]) => (
          <div key={role} className="card p-3 text-center">
            <Badge variant={ROLE_VARIANTS[role]} className="mb-1">{label}</Badge>
            <p className="text-2xs text-stone-400 mt-1">{ROLE_EMAIL[role]}</p>
          </div>
        ))}
      </div>

      <div className="card overflow-hidden">
        <table className="w-full text-sm">
          <thead>
            <tr className="border-b border-stone-200 bg-stone-50">
              {['Name','Role','Phone','Login Email','Last Seen',''].map((h, i) => (
                <th key={i} className="px-4 py-2.5 text-left text-xs font-semibold text-stone-500 uppercase tracking-wide">{h}</th>
              ))}
            </tr>
          </thead>
          <tbody className="divide-y divide-stone-100">
            {active.map(m => <MemberRow key={m.id} m={m} />)}
            {inactive.length > 0 && (
              <>
                <tr className="bg-stone-50">
                  <td colSpan={6} className="px-4 py-1.5 text-xs font-semibold uppercase tracking-wide text-stone-400">
                    Inactive
                  </td>
                </tr>
                {inactive.map(m => <MemberRow key={m.id} m={m} />)}
              </>
            )}
          </tbody>
        </table>
      </div>

      <div className="card p-4">
        <h3 className="text-sm font-semibold text-stone-700 mb-2">Role Permissions</h3>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-8 gap-y-1 text-xs text-stone-500">
          <p><strong className="text-stone-700">Owner</strong> — full access to everything</p>
          <p><strong className="text-stone-700">GM</strong> — products, catering, ops, team, settings</p>
          <p><strong className="text-stone-700">Operations</strong> — products, catering, inventory, orders, store supply</p>
          <p><strong className="text-stone-700">Chef</strong> — products (view), inventory, operations, store supply</p>
          <p><strong className="text-stone-700">Marketing</strong> — catering, marketing pages</p>
        </div>
      </div>
    </div>
  )
}
